import { useState } from 'react';
import type { BulkActionType, MachineUpdate } from '../types';

interface UseBulkSelectionReturn {
  isBulkMode: boolean;
  selectedBulkIds: Set<string>;
  bulkDialogAction: BulkActionType | null;
  setBulkDialogAction: (action: BulkActionType | null) => void;
  bulkSaving: boolean;
  toggleBulkMode: () => void;
  toggleBulkSelect: (id: string) => void;
  selectAll: (ids: string[]) => void;
  deselectAll: () => void;
  saveBulkAction: (updates: MachineUpdate) => Promise<boolean>;
}

export function useBulkSelection(
  bulkUpdateMachines: (ids: string[], updates: MachineUpdate) => Promise<void>
): UseBulkSelectionReturn {
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedBulkIds, setSelectedBulkIds] = useState<Set<string>>(new Set());
  const [bulkDialogAction, setBulkDialogAction] = useState<BulkActionType | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  function toggleBulkMode() {
    setIsBulkMode((v) => !v);
    setSelectedBulkIds(new Set());
    setBulkDialogAction(null);
  }

  function toggleBulkSelect(id: string) {
    setSelectedBulkIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAll(ids: string[]) {
    setSelectedBulkIds(new Set(ids));
  }

  function deselectAll() {
    setSelectedBulkIds(new Set());
  }

  async function saveBulkAction(updates: MachineUpdate): Promise<boolean> {
    setBulkSaving(true);
    try {
      await bulkUpdateMachines(Array.from(selectedBulkIds), updates);
      setIsBulkMode(false);
      setSelectedBulkIds(new Set());
      setBulkDialogAction(null);
      return true;
    } catch {
      return false;
    } finally {
      setBulkSaving(false);
    }
  }

  return {
    isBulkMode,
    selectedBulkIds,
    bulkDialogAction,
    setBulkDialogAction,
    bulkSaving,
    toggleBulkMode,
    toggleBulkSelect,
    selectAll,
    deselectAll,
    saveBulkAction,
  };
}
